import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "i18next";
import { enqueueSnackbar } from "notistack";

import {
    reverseSessionSubdomain,
    getSubdomainFromSession,
    getCustomization,
} from "components/helper/callApi";
import { useSubdomain } from "components/helper/SubdomainContext";

import {
    buildVersionedFootprint,
    clearCustomization,
    Customization,
    saveCustomization,
    Subdomain,
} from "services/customizationService";
import {
    getCodeOnlineFromPath,
    getSessionSubdomainRedirectUrl,
    getSubdomain,
} from "services/urlService";

import { ERROR } from "types/snackBar.constant";
import { SessionHostCheck, SubdomainHandlerProps } from "types/subdomainType";
import { LS_FOOTPRINT_KEY } from "constants/constants";

// Outside of the component, with the global snackbar and i18n instances: the handling does not depend on them, a
// language change must not run it again
function showUnknownError() {
    enqueueSnackbar(i18n.t("errorUnknown"), {
        variant: ERROR,
    });
}

// Code of the session associated with the subdomain, or null when there is none
async function getSubdomainCodeOnline(): Promise<string | null> {
    try {
        return await reverseSessionSubdomain();
    } catch (error) {
        showUnknownError();
        return null;
    }
}

function handleCustomization(customization: Customization) {
    try {
        const existingFootprint = localStorage.getItem(LS_FOOTPRINT_KEY);

        if (
            buildVersionedFootprint(customization.footprint) ===
            existingFootprint
        ) {
            // Entity didn't changed, no need to save it
            return;
        }

        saveCustomization(customization);
    } catch (error) {
        showUnknownError();
    }
}

const SubdomainHandler = ({ children }: SubdomainHandlerProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setSubdomainHandlingCompleted } = useSubdomain();
    // Before the hooks, which read them in their dependencies.
    // From the router location, so that a code typed on the home page (client side navigation) is checked too.
    const codeOnline = getCodeOnlineFromPath(location.pathname);
    const isHomePage = location.pathname === "/";

    // navigate changes on every route change: read through a ref, so that the handling only runs again when the code
    // or the home page changes (not from the login to the session route)
    const navigateRef = useRef(navigate);

    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    useEffect(() => {
        // Set by the cleanup when the route changes again: this run must stop, the newer one takes over
        let isOutdated = false;
        // The routes stay hidden while the page is left, or handed over to the next run: they could be displayed with
        // the design of another entity meanwhile
        let keepLoading = false;

        const baseUrl = window.env.REACT_APP_ONLINE_API_BASE_URL as string;
        const subdomain = getSubdomain();
        // From the browser rather than the router location, which would run the handling again on every route change
        const currentPath = `${window.location.pathname}${window.location.search}`;

        function redirect(url: string) {
            // The newer run decides where the participant goes
            if (isOutdated) {
                return;
            }

            keepLoading = true;
            window.location.replace(url);
        }

        async function checkSessionHost(
            sessionCodeOnline: string,
        ): Promise<SessionHostCheck> {
            let sessionSubdomain: Subdomain | null;
            try {
                sessionSubdomain =
                    await getSubdomainFromSession(sessionCodeOnline);
            } catch (error) {
                return "entityUnknown";
            }

            if (!sessionSubdomain) {
                return "stay";
            }

            const redirectUrl = getSessionSubdomainRedirectUrl({
                baseUrl,
                currentHostname: window.location.hostname,
                path: currentPath,
                sessionSubdomain: sessionSubdomain.subdomain,
            });
            if (!redirectUrl) {
                return "stay";
            }

            redirect(redirectUrl);
            return "redirected";
        }

        async function handleWithoutSubdomain() {
            // Without a subdomain the entity is unknown: a customization stored earlier (socket update,
            // previous visit) would otherwise be applied to every session of this origin.
            clearCustomization();

            // Whatever the result, the default design applied above is the expected one on this page
            if (codeOnline) {
                await checkSessionHost(codeOnline);
            }
        }

        async function handleWithSubdomain() {
            const customization: Customization | null =
                await getCustomization();
            if (isOutdated) {
                return;
            }

            if (!customization) {
                // No customization for this entity: the previous one must not stay displayed.
                clearCustomization();

                if (!baseUrl.includes("local")) {
                    redirect(`${baseUrl}${currentPath}`);
                    return;
                }
            }

            // The subdomain may belong to another entity than the session's one: checked before saving its
            // customization, otherwise the participant would get the design of the wrong entity.
            if (codeOnline) {
                const sessionHostCheck = await checkSessionHost(codeOnline);
                if (isOutdated || sessionHostCheck === "redirected") {
                    return;
                }

                if (sessionHostCheck === "entityUnknown") {
                    // Default design rather than the one of a subdomain that may belong to another entity
                    clearCustomization();
                    return;
                }
            }

            if (isHomePage) {
                const subdomainCodeOnline = await getSubdomainCodeOnline();
                if (isOutdated) {
                    return;
                }

                if (subdomainCodeOnline) {
                    // The run of the login route checks this code, then saves the customization
                    keepLoading = true;
                    navigateRef.current(`/login/${subdomainCodeOnline}`);
                    return;
                }
            }

            if (customization) {
                handleCustomization(customization);
            }
        }

        async function init() {
            setSubdomainHandlingCompleted(false);

            try {
                if (subdomain) {
                    await handleWithSubdomain();
                } else {
                    await handleWithoutSubdomain();
                }
            } catch (error) {
                if (isOutdated) {
                    return;
                }
                // Network or API error on the customization: default design rather than the subdomain's one
                clearCustomization();
                showUnknownError();
            } finally {
                if (!isOutdated && !keepLoading) {
                    setSubdomainHandlingCompleted(true);
                }
            }
        }

        init();

        return () => {
            isOutdated = true;
        };
    }, [codeOnline, isHomePage, setSubdomainHandlingCompleted]);

    return children;
};

export default SubdomainHandler;
